import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Layout,
  Link as LinkIcon,
  Save,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Info,
} from 'lucide-react'
import { apiGet, apiPatch } from '../lib/api'

/**
 * Footer autoadministrable — edita los 3 encabezados y las 3 listas de
 * links (catálogo, ayuda, legal) que el Footer público renderiza.
 *
 * Toda la data vive en la tabla `config` con claves Footer_* y se sirve
 * vía /api/admin/footer-settings.
 */

interface FooterLink {
  label: string
  url: string
  target?: '_blank'
  requiresAuth?: boolean
}

interface FooterPayload {
  headings: { catalog: string; help: string; contact: string }
  links: { catalog: FooterLink[]; help: FooterLink[]; legal: FooterLink[] }
}

type ListKey = 'catalog' | 'help' | 'legal'

const LIST_META: Record<ListKey, { title: string; hint: string }> = {
  catalog: {
    title: 'Links de Catálogo',
    hint: 'Aparecen bajo el encabezado "Catálogo" del footer. Suelen ser categorías destacadas.',
  },
  help: {
    title: 'Links de Ayuda',
    hint: 'Aparecen bajo el encabezado "Ayuda". Por ej. FAQ, envíos, devoluciones.',
  },
  legal: {
    title: 'Links Legales',
    hint: 'Aparecen al pie del footer. Términos, privacidad, aviso legal, etc.',
  },
}

function emptyLink(): FooterLink {
  return { label: '', url: '' }
}

export function Footer() {
  const qc = useQueryClient()
  const [draft, setDraft] = useState<FooterPayload | null>(null)
  const [flash, setFlash] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null)

  const { data, isLoading } = useQuery<FooterPayload>({
    queryKey: ['admin-footer-settings'],
    queryFn: () => apiGet<FooterPayload>('/api/admin/footer-settings'),
  })

  useEffect(() => {
    if (data && !draft) {
      setDraft({
        headings: { ...data.headings },
        links: {
          catalog: data.links.catalog.map((l) => ({ ...l })),
          help: data.links.help.map((l) => ({ ...l })),
          legal: data.links.legal.map((l) => ({ ...l })),
        },
      })
    }
  }, [data, draft])

  const saveMut = useMutation({
    mutationFn: (payload: FooterPayload) =>
      apiPatch<{ ok: boolean; updated: number }>('/api/admin/footer-settings', payload),
    onSuccess: (r) => {
      setFlash({ tipo: 'ok', msg: `Footer guardado (${r.updated} campo(s))` })
      qc.invalidateQueries({ queryKey: ['admin-footer-settings'] })
      setTimeout(() => setFlash(null), 3000)
    },
    onError: (e: any) =>
      setFlash({ tipo: 'err', msg: e?.message || 'Error al guardar el footer' }),
  })

  function setHeading(key: keyof FooterPayload['headings'], value: string) {
    if (!draft) return
    setDraft({ ...draft, headings: { ...draft.headings, [key]: value } })
  }

  function updateLink(list: ListKey, idx: number, patch: Partial<FooterLink>) {
    if (!draft) return
    const arr = [...draft.links[list]]
    arr[idx] = { ...arr[idx], ...patch }
    setDraft({ ...draft, links: { ...draft.links, [list]: arr } })
  }

  function addLink(list: ListKey) {
    if (!draft) return
    setDraft({
      ...draft,
      links: { ...draft.links, [list]: [...draft.links[list], emptyLink()] },
    })
  }

  function removeLink(list: ListKey, idx: number) {
    if (!draft) return
    const arr = draft.links[list].filter((_, i) => i !== idx)
    setDraft({ ...draft, links: { ...draft.links, [list]: arr } })
  }

  function moveLink(list: ListKey, idx: number, dir: -1 | 1) {
    if (!draft) return
    const arr = [...draft.links[list]]
    const j = idx + dir
    if (j < 0 || j >= arr.length) return
    ;[arr[idx], arr[j]] = [arr[j], arr[idx]]
    setDraft({ ...draft, links: { ...draft.links, [list]: arr } })
  }

  function onSave() {
    if (!draft) return
    // Limpiar links con label/url vacíos antes de enviar.
    const cleaned: FooterPayload = {
      headings: draft.headings,
      links: {
        catalog: draft.links.catalog.filter((l) => l.label.trim() && l.url.trim()),
        help: draft.links.help.filter((l) => l.label.trim() && l.url.trim()),
        legal: draft.links.legal.filter((l) => l.label.trim() && l.url.trim()),
      },
    }
    saveMut.mutate(cleaned)
  }

  if (isLoading || !draft) {
    return (
      <div className="p-6">
        <div className="grid place-items-center rounded-xl border border-ink-200 bg-white p-12 text-ink-500">
          <Loader2 className="size-6 animate-spin" />
          <span className="mt-2 text-sm">Cargando configuración del footer…</span>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl p-6">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid size-11 place-items-center rounded-lg bg-brand-50 text-brand-600">
            <Layout className="size-5" strokeWidth={2.2} />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-ink-900">Footer del sitio</h1>
            <p className="text-sm text-ink-500">
              Encabezados y listas de links del pie de página público.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onSave}
          disabled={saveMut.isPending}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saveMut.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Save className="size-4" />
          )}
          Guardar cambios
        </button>
      </div>

      {flash && (
        <div
          className={`mb-4 flex items-center gap-2 rounded-lg border px-4 py-3 text-sm ${
            flash.tipo === 'ok'
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {flash.tipo === 'ok' ? (
            <CheckCircle2 className="size-4" />
          ) : (
            <AlertCircle className="size-4" />
          )}
          <span>{flash.msg}</span>
        </div>
      )}

      {/* Encabezados */}
      <section className="mb-6 rounded-xl border border-ink-200 bg-white p-5">
        <h2 className="mb-1 text-base font-bold text-ink-900">Encabezados</h2>
        <p className="mb-4 flex items-start gap-1.5 text-xs text-ink-500">
          <Info className="mt-0.5 size-3.5 shrink-0" />
          <span>Títulos de las columnas que se ven en el footer del sitio público.</span>
        </p>
        <div className="grid gap-4 sm:grid-cols-3">
          <HeadingInput
            label="Catálogo"
            value={draft.headings.catalog}
            onChange={(v) => setHeading('catalog', v)}
            placeholder="Catálogo"
          />
          <HeadingInput
            label="Ayuda"
            value={draft.headings.help}
            onChange={(v) => setHeading('help', v)}
            placeholder="Ayuda"
          />
          <HeadingInput
            label="Contacto"
            value={draft.headings.contact}
            onChange={(v) => setHeading('contact', v)}
            placeholder="Contacto"
          />
        </div>
      </section>

      {/* 3 listas de links */}
      {(['catalog', 'help', 'legal'] as const).map((list) => (
        <section key={list} className="mb-6 rounded-xl border border-ink-200 bg-white p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="flex items-center gap-2 text-base font-bold text-ink-900">
                <LinkIcon className="size-4 text-brand-600" />
                {LIST_META[list].title}
              </h2>
              <p className="mt-1 text-xs text-ink-500">{LIST_META[list].hint}</p>
            </div>
            <button
              type="button"
              onClick={() => addLink(list)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-bold text-ink-700 transition-colors hover:bg-ink-50"
            >
              <Plus className="size-3.5" />
              Agregar link
            </button>
          </div>

          {draft.links[list].length === 0 ? (
            <div className="rounded-lg border border-dashed border-ink-200 px-4 py-6 text-center text-xs text-ink-400">
              No hay links en esta sección. Da clic en "Agregar link" para crear uno.
            </div>
          ) : (
            <ul className="space-y-2">
              {draft.links[list].map((link, idx) => (
                <li
                  key={idx}
                  className="rounded-lg border border-ink-200 bg-ink-50/40 p-3"
                >
                  <div className="flex flex-wrap items-start gap-2">
                    <div className="flex flex-col gap-1 pt-1">
                      <button
                        type="button"
                        title="Subir"
                        onClick={() => moveLink(list, idx, -1)}
                        disabled={idx === 0}
                        className="grid size-6 place-items-center rounded border border-ink-200 bg-white text-ink-500 transition-colors hover:bg-ink-100 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChevronUp className="size-3.5" />
                      </button>
                      <button
                        type="button"
                        title="Bajar"
                        onClick={() => moveLink(list, idx, 1)}
                        disabled={idx === draft.links[list].length - 1}
                        className="grid size-6 place-items-center rounded border border-ink-200 bg-white text-ink-500 transition-colors hover:bg-ink-100 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChevronDown className="size-3.5" />
                      </button>
                    </div>

                    <div className="grid flex-1 gap-2 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-ink-500">
                          Texto
                        </label>
                        <input
                          type="text"
                          value={link.label}
                          onChange={(e) =>
                            updateLink(list, idx, { label: e.target.value })
                          }
                          placeholder="Ej. Llantas para auto"
                          className="block w-full rounded-md border border-ink-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-ink-500">
                          URL
                        </label>
                        <input
                          type="text"
                          value={link.url}
                          onChange={(e) =>
                            updateLink(list, idx, { url: e.target.value })
                          }
                          placeholder="/llantas o https://..."
                          className="block w-full rounded-md border border-ink-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      title="Eliminar link"
                      onClick={() => removeLink(list, idx)}
                      className="mt-1 grid size-8 place-items-center rounded-md border border-red-200 bg-white text-red-600 transition-colors hover:bg-red-50"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </div>

                  <div className="mt-2 flex flex-wrap items-center gap-4 pl-9 text-xs text-ink-500">
                    <label className="inline-flex cursor-pointer items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={link.target === '_blank'}
                        onChange={(e) =>
                          updateLink(list, idx, {
                            target: e.target.checked ? '_blank' : undefined,
                          })
                        }
                        className="size-3.5 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                      />
                      Abrir en pestaña nueva
                    </label>
                    <label className="inline-flex cursor-pointer items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={!!link.requiresAuth}
                        onChange={(e) =>
                          updateLink(list, idx, { requiresAuth: e.target.checked })
                        }
                        className="size-3.5 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                      />
                      Solo visible si hay sesión iniciada
                    </label>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
        Los cambios se aplican <strong>inmediatamente</strong> en el footer del sitio público
        después de guardar. No requiere deploy ni reinicio del servidor.
      </div>
    </div>
  )
}

interface HeadingInputProps {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder: string
}

function HeadingInput({ label, value, onChange, placeholder }: HeadingInputProps) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-ink-500">
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="block w-full rounded-md border border-ink-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
      />
    </div>
  )
}
